/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState, useCallback, startTransition } from 'react';
import { TreeSelect, Spin, Input, Button } from 'antd';
import { ChevronDown, ChevronRight, SearchIco, SelectIco } from '@/components/Icons/IconMenu';
import type { RefObject } from 'react';
import { usePrivateStore } from '@/store/usePrivateStore';
import { getOrganizationIndexedDB, type OrganizationData } from '@/utils/organizationIndexedDBUtils';
import { TeamOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/store/useAuthStore';
import { useOrgScopeOptional } from '@/contexts/OrgScopeContext';
import {
  findAncestorCompanyDepartment,
} from '@/utils/orgScope';
import {
  extractTextLabel,
  getDescendantIds,
  getParentId,
  getSiblingIds,
  getAncestorIds,
  type SimpleTreeNode,
} from '../helper';

/** So khớp search: không phân biệt dấu (NFD + đ/Đ → d) */
function normalizeSearchTextForMatch(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'd')
    .toLowerCase();
}

const SEARCH_CHUNK_SIZE = 500;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_CHARS = 2;

function yieldToMain(): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, 0);
  });
}

const SearchField = React.memo(function SearchField({
  draftRef,
  onCancelInFlight,
  onDebouncedChange,
}: {
  draftRef: React.MutableRefObject<string>;
  onCancelInFlight: () => void;
  onDebouncedChange: (kw: string) => void;
}) {
  const [value, setValue] = useState(draftRef.current);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = value.trim();
      onDebouncedChange(trimmed.length >= SEARCH_MIN_CHARS ? trimmed : '');
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [value, onDebouncedChange]);

  return (
    <Input
      size="large"
      allowClear
      placeholder="Tìm kiếm"
      value={value}
      onChange={(e) => {
        onCancelInFlight();
        const next = e.target.value;
        draftRef.current = next;
        setValue(next);
      }}
      onClick={(e) => e.stopPropagation()}
      className="mb-4 !text-sm"
      prefix={<SearchIco />}
    />
  );
});

function getEmployeeIdFromEmpDepKey(raw: string | undefined): string {
  if (!raw || !raw.startsWith('emp_') || !raw.includes('_dep_')) return '';
  return raw.split('_dep_')[0]?.replace(/^emp_/, '') ?? '';
}

/** Route buoc4/buoc5: không chọn NV khác nhưng vẫn hiển thị NV đang login */
function isSelfEmployeeSelection(
  rawValue: string | undefined,
  profileId: string | undefined,
  bpOriId: string | undefined
): boolean {
  if (!rawValue?.startsWith('emp_') || !profileId) return false;
  const empId = getEmployeeIdFromEmpDepKey(rawValue);
  if (empId !== String(profileId)) return false;
  if (!bpOriId) return true;
  const depPart = rawValue.split('_dep_')[1];
  return !depPart || depPart === String(bpOriId);
}

type DepEmpTreeSelectProps = {
  tenantID: string;
  initialId?: string;
  excludeId?: string;
  placeholder?: string;
  externalValue?: { value: string; label: string } | undefined;
  onChange?: (value: { value: string; label: string } | undefined) => void;
  visible?: boolean;
  hasLoadedInitialDataRef?: RefObject<boolean>;
  autoSelectFirst?: boolean;
  parentID?: string;
  roleGroupIDs?: string;
  viewMode?: string;
  routeCongTy?: boolean;
  /** VH (trừ Đào tạo/AP): cây chỉ 1 node công ty cha, không NV. */
  vanHoaCompanyOnlyTree?: boolean;
  isShowEmployee?: boolean;
  companyData?: OrganizationData | null;
};

const DepEmpTreeSelect: React.FC<DepEmpTreeSelectProps> = ({
  tenantID,
  // initialId,
  placeholder = 'Chọn bộ phận',
  externalValue,
  onChange,
  visible = true,
  autoSelectFirst = true,
  routeCongTy = false,
  vanHoaCompanyOnlyTree = false,
  isShowEmployee = false,
  companyData,
  viewMode,
}) => {
  const { setTreeSelectDepartment } = usePrivateStore();

  const [orgData, setOrgData] = useState<OrganizationData | null>(companyData ?? null);
  const [loading, setLoading] = useState<boolean>(!companyData);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchGenRef = useRef(0);
  const searchDraftRef = useRef('');
  const [searchMatch, setSearchMatch] = useState<{
    deptIds: Set<string>;
    deptTitleIds: Set<string>;
    empIds: Set<string>;
    empDeptIds: string[];
  } | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [searchExpandedKeys, setSearchExpandedKeys] = useState<string[]>([]);
  const [employeeExpandedKeys, setEmployeeExpandedKeys] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [value, setValue] = useState<{ value: string; label: string } | undefined>(externalValue);
  const profile = useAuthStore((state) => state.profile);
  const orgScopeCtx = useOrgScopeOptional();
  const isAdmin = orgScopeCtx ? orgScopeCtx.orgScope.useFullOrg : Boolean(profile?.isAdmin);
  const isQuanLy = orgScopeCtx ? orgScopeCtx.orgScope.treatAsQuanLy : Boolean(profile?.isQuanLy);
  const isNhanVien = !isAdmin && !isQuanLy;
  const bpOriID = profile?.boPhanId;
  /** NV thường (profile): route buoc5 không show tree NV nhưng vẫn hiển thị tên mình */
  const isPlainEmployee =
    Boolean(profile?.id) &&
    !profile?.isAdmin &&
    !profile?.isQuanLy &&
    !(orgScopeCtx?.orgScope.useFullOrg ?? false) &&
    !(orgScopeCtx?.orgScope.treatAsQuanLy ?? false);
  const lockEmployeeInDropdown = isPlainEmployee && !isShowEmployee;

  useEffect(() => {
    if (companyData) {
      setOrgData(companyData);
      setLoading(false);
    }
  }, [companyData]);

  const cancelInFlightSearch = useCallback(() => {
    searchGenRef.current += 1;
  }, []);

  const handleDebouncedSearch = useCallback((kw: string) => {
    setDebouncedSearchTerm((prev) => (prev === kw ? prev : kw));
  }, []);

  useEffect(() => {
    if (externalValue === undefined || !orgData) return;
    const keepSelfEmp =
      lockEmployeeInDropdown ||
      isSelfEmployeeSelection(externalValue.value, profile?.id, bpOriID);
    if (externalValue.value.includes('emp_') && !isShowEmployee && !keepSelfEmp) {
      // Route không cho NV: map emp → bộ phận của NV (từ key emp_*_dep_*)
      const depId = externalValue.value.includes('_dep_')
        ? externalValue.value.split('_dep_')[1]
        : '';
      const allDept = Array.isArray(orgData.departments)
        ? orgData.departments
        : Object.values(orgData.departments || {});
      const dept = depId
        ? allDept.find((d: any) => String(d.ID ?? d.id) === String(depId))
        : undefined;
      if (dept) {
        const deptValue = {
          value: String(depId),
          label: String((dept as any).Ten ?? (dept as any).ten ?? ''),
        };
        setValue(deptValue);
        onChange?.(deptValue);
        setTreeSelectDepartment({
          selectedValue: deptValue,
          tenantID: tenantID || '',
          idSelect: String(depId),
          isEmployee: false,
          parentID: String((dept as any).ParentID ?? (dept as any).parentID ?? '0'),
          isCompany:
            (dept as any).Key === 1 ||
            (dept as any).IsCompany ||
            (dept as any).isCompany ||
            false,
        });
      } else {
        setValue(undefined);
      }
    } else {
      setValue(externalValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalValue, isShowEmployee, lockEmployeeInDropdown, profile?.id, bpOriID, orgData, tenantID]);

  useEffect(() => {
    if (companyData) {
      setOrgData(companyData);
      setLoading(false);
    }
  }, [companyData]);

  // Load organization data only from IndexedDB (không gọi API)
  useEffect(() => {
    if (companyData || !visible) return;

    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getOrganizationIndexedDB();
          if (!mounted) return;
        setOrgData(data);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [companyData, visible]);
  const departments = useMemo(() => orgData?.departments || [], [orgData]);

  const nonManagerEmployees = useMemo(
    () => (orgData?.employees ?? []).filter((e: any) => !(e?.IsQuanLy ?? e?.isQuanLy)),
    [orgData],
  );

  const empsByDeptId = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const e of nonManagerEmployees) {
      const depId = String(e.BPID ?? '');
      if (!depId) continue;
      const list = map.get(depId);
      if (list) list.push(e);
      else map.set(depId, [e]);
    }
    return map;
  }, [nonManagerEmployees]);

  const deptIdsWithEmployees = useMemo(() => new Set(empsByDeptId.keys()), [empsByDeptId]);

  // Parse viewMode - use primitive values to avoid reference changes
  const ngangcap = useMemo(() => {
    if (!viewMode) return false;
    const parts = viewMode.split(',');
    for (const part of parts) {
      const [key, value] = part.split(':');
      if (key === 'ngangcap') return value === 'true';
    }
    return false;
  }, [viewMode]);

  const vuotcap = useMemo(() => {
    if (!viewMode) return false;
    const parts = viewMode.split(',');
    for (const part of parts) {
      const [key, value] = part.split(':');
      if (key === 'vuotcap') return value === 'true';
    }
    return false;
  }, [viewMode]);

  const viewModeParsed = useMemo(() => ({ ngangcap, vuotcap }), [ngangcap, vuotcap]);
  //============================
  //* === TREE DATA ===
  //============================
  const treeData: SimpleTreeNode[] = useMemo(() => {
    if (!orgData) return [];

    const allDept = orgData.departments || [];

    if (vanHoaCompanyOnlyTree) {
      const anchorDept =
        findAncestorCompanyDepartment(bpOriID, allDept)
        ?? allDept.find((d: any) => String(d.ID ?? d.id) === String(externalValue?.value ?? ''));
      const anchorId = String(anchorDept?.ID ?? anchorDept?.id ?? '');

      // Trưởng phòng / nhân viên VH: chỉ 1 công ty cha (admin VH dùng routeCongTy — luồng BSC buoc1)
      const rawValue = String(externalValue?.value ?? anchorId ?? '');
      const companyId = rawValue.includes('_dep_') ? rawValue.split('_dep_').pop() || '' : rawValue;
      if (!companyId) return [];

      const dept = allDept.find((d: any) => String(d.ID ?? d.id) === companyId);
      if (!dept) return [];

      const title = String(externalValue?.label || (dept as any).Ten || (dept as any).ten || '');
      return [{
        id: companyId,
        pId: '0',
        title,
        isLeaf: true,
        isCompany: true,
        key: companyId,
        hideEmployeeIcon: true,
      }];
    }

    // Tính xem department có child department không để set isLeaf chính xác
    const parentIdMap = new Map<string, boolean>();
    allDept.forEach((d: any) => {
      const pId = d.ParentID && d.ParentID !== '0' ? String(d.ParentID) : '0';
      if (pId) parentIdMap.set(pId, true);
    });

    const deptNodes: SimpleTreeNode[] = allDept.map((d: any) => {
      const id = String(d.ID ?? d.id);
      const pId = d.ParentID && d.ParentID !== '0' ? String(d.ParentID) : '0';
      const hasChildDept = parentIdMap.has(id);

      return {
        id,
        pId,
        title: String(d.Ten ?? d.ten ?? ''),
        isLeaf: !hasChildDept, // node không có child dept -> leaf, sẽ ẩn icon expand
        isCompany: d.IsCompany ?? d.isCompany,
        key: id,
      };
    });

    let allNodes: SimpleTreeNode[] = [...deptNodes];

    // ROUTE CÔNG TY
    if (routeCongTy) {
      const currentBPId = externalValue?.value;

      // Nếu không có externalValue, ẩn toàn bộ
      if (!currentBPId) return [];
      // Tìm bộ phận hiện tại từ externalValue (bộ phận được chọn)
      const currentBP = allDept.find((d: any) => String(d.ID ?? d.id) === String(currentBPId)) as any;
      if (!currentBP) return [];
      // Admin: xem toàn bộ bộ phận có key = 1 (loại công ty)
      if (isAdmin) {
        // Filter các nodes có Key === 1 và isCompany === true
        allNodes = allNodes.filter((node) => {
          const dept = allDept.find((d: any) => String(d.ID ?? d.id) === node.id);
          return dept?.Key === 1 && node.isCompany === true;
        });
        // Loại bỏ các nodes có parentID không tồn tại trong allNodes
        // Lặp lại cho đến khi không còn node nào bị loại (xử lý trường hợp nhiều cấp)
        allNodes = allNodes.map((node) => {
          const hasChildInFiltered = allNodes.some((n) => n.pId === node.id);
          return {
            ...node,
            isLeaf: !hasChildInFiltered,
          };
        });
        return allNodes;
      }
      // Không phải admin: xác định quyền dựa trên bpOriID (bộ phận gốc của user)
      if (!bpOriID) return [];
      const bpOriIDStr = String(bpOriID);
      const bpOri = allDept.find((d: any) => String(d.ID ?? d.id) === bpOriIDStr) as any;
      if (!bpOri) return [];
      const allowedNodeIds = new Set<string>();
      // Trường hợp 1: Bộ phận gốc của user là loại công ty (Key = 1)
      if (bpOri?.Key === 1) {
        // Xem được chính nó
        allowedNodeIds.add(bpOriIDStr);

        // Nếu có xem vượt cấp: xem được parent nếu parent là công ty key = 1
        if (viewModeParsed.vuotcap) {
          const parentId = isNhanVien
            ? String(bpOri.ID ?? bpOri.id ?? '0') // nhân viên thì lấy ID của bộ phận gốc làm parent
            : String(bpOri.ParentID ?? bpOri.parentID ?? '0'); // còn lại thì lấy ParentID

          if (parentId && parentId !== '0') {
            const parent = allDept.find((d: any) => String(d.ID ?? d.id) === parentId);
            if (parent?.Key === 1) {
              allowedNodeIds.add(parentId);
            }
          }
        }

        // Nếu có ngang cấp: thấy được các siblings loại công ty key = 1
        if (viewModeParsed.ngangcap) {
          const baseParentId = String(bpOri.ParentID ?? bpOri.parentID ?? '0');
          const siblings = allDept
            .filter((d: any) => {
              const dParentId = String(d.ParentID ?? d.parentID ?? '0');
              return dParentId === baseParentId && String(d.ID ?? d.id) !== bpOriIDStr && d.Key === 1;
            })
            .map((d: any) => String(d.ID ?? d.id));
          siblings.forEach((siblingId) => allowedNodeIds.add(siblingId));
        }
      }
      // Trường hợp 2: Bộ phận gốc của user không phải công ty (Key != 1)
      // Nếu có quyền vượt cấp: hiển thị parent nếu là công ty
      else if (viewModeParsed.vuotcap) {
        const parentId = isNhanVien
          ? String(bpOri.ID ?? bpOri.id ?? '0') // nhân viên thì lấy ID của bộ phận gốc làm parent
          : String(bpOri.ParentID ?? bpOri.parentID ?? '0'); // còn lại thì lấy ParentID
        if (parentId && parentId !== '0') {
          const parent = allDept.find((d: any) => String(d.ID ?? d.id) === parentId);
          if (parent?.Key === 1) {
            allowedNodeIds.add(parentId);
          }
        }
      }
      // Chỉ hiển thị bộ phận được chọn (currentBP) nếu nó nằm trong danh sách được phép
      // hoặc nếu nó là công ty (Key = 1) và user có quyền xem
      if (currentBP?.Key === 1 && allowedNodeIds.has(String(currentBP.ID ?? currentBP.id))) {
        // Hiển thị bộ phận được chọn và các node được phép
        allNodes = allNodes.filter((node) => {
          const dept = allDept.find((d: any) => String(d.ID ?? d.id) === node.id);
          return allowedNodeIds.has(node.id) && dept?.Key === 1 && node.isCompany === true;
        });
        allNodes = allNodes.map((node) => {
          const hasChildInFiltered = allNodes.some((n) => n.pId === node.id);
          return {
            ...node,
            isLeaf: !hasChildInFiltered,
          };
        });
        return allNodes;
      }
      // Không hiển thị trên tree
      return [];
    }

    // ROUTE THƯỜNG (quản lí - nhân viên)
    if (!isAdmin && bpOriID) {
      const bpOriIDStr = bpOriID ? String(bpOriID) : undefined;
      if (!bpOriIDStr) return allNodes;
      const allowedNodeIds = new Set<string>();
      let siblingIdsForLeaf: string[] = []; // Lưu sibling IDs để set isLeaf sau khi filter

      if (isQuanLy) {
        // Trưởng phòng: ban đầu thấy tree từ bộ phận của mình trực thuộc
        allowedNodeIds.add(bpOriIDStr);
        const descendants = getDescendantIds(bpOriIDStr, allNodes);
        descendants.forEach((id) => allowedNodeIds.add(id));

        // VUOTCAP: thấy được phòng ban cao hơn 1 cấp
        if (vuotcap) {
          const parentId = isNhanVien ? bpOriIDStr : getParentId(bpOriIDStr, allNodes);
          if (parentId) {
            allowedNodeIds.add(parentId);
          }
        }

        // ngangcap: thấy được các phòng ban có cùng parentID (siblings), nhưng không thấy children của siblings
        if (ngangcap) {
          siblingIdsForLeaf = getSiblingIds(bpOriIDStr, allNodes);
          siblingIdsForLeaf.forEach((id) => allowedNodeIds.add(id));
        }
      } else if (isNhanVien) {
        // Nhân viên:
        // - có vuotcap: thấy được phòng ban (bộ phận mình phụ trách) và node nhân viên là chính nó
        // - có ngangcap: chỉ thấy các nhân viên cùng phòng + chính mình (không có node phòng ban)
        // - không có vuotcap và không ngangcap: chỉ thấy mỗi bản thân là một node riêng
        if (vuotcap) {
          allowedNodeIds.add(bpOriIDStr);
        }
     
      }

      // FILTER NODES has id
      allNodes = allNodes.filter((node) => allowedNodeIds.has(node.id));

      // Nếu ngangcap: set isLeaf: true cho siblings để ẩn icon expand (vì không thấy children của siblings)
      if (isQuanLy && ngangcap && siblingIdsForLeaf.length > 0) {
        allNodes = allNodes.map((node) => {
          if (siblingIdsForLeaf.includes(node.id)) {
            return { ...node, isLeaf: true, hideEmployeeIcon: true };
          }
          return node;
        });
      }

      // Nếu có cả vuotcap và ngangcap: ẩn icon nhân viên của parent và siblings
      if (isQuanLy && vuotcap && ngangcap) {
        const parentId = getParentId(bpOriIDStr, allNodes);
        const nodesToHideIcon = new Set<string>();
        if (parentId) {
          nodesToHideIcon.add(parentId);
        }
        siblingIdsForLeaf.forEach((id) => nodesToHideIcon.add(id));
        allNodes = allNodes.map((node) => {
          if (nodesToHideIcon.has(node.id)) {
            return { ...node, hideEmployeeIcon: true };
          }
          return node;
        });
      }
    }

    return allNodes;
  }, [
    orgData,
    routeCongTy,
    isAdmin,
    isQuanLy,
    isNhanVien,
    bpOriID,
    vanHoaCompanyOnlyTree,
    externalValue?.value,
    externalValue?.label,
    ngangcap,
    vuotcap,
  ]);

  const searchIndex = useMemo(() => {
    if (!orgData) {
      return {
        depts: [] as { id: string; normTitle: string }[],
        emps: [] as { id: string; depId: string; normTitle: string; normMa: string }[],
      };
    }
    const allDept = orgData.departments || [];
    const depts = allDept.map((d: any) => ({
      id: String(d.ID ?? d.id),
      normTitle: normalizeSearchTextForMatch(String(d.Ten ?? d.ten ?? '')),
    }));
    const emps = isShowEmployee
      ? nonManagerEmployees.map((e: any) => ({
          id: String(e.ID ?? e.id),
          depId: String(e.BPID ?? ''),
          normTitle: normalizeSearchTextForMatch(String(e.TenNhanVien ?? '')),
          normMa: normalizeSearchTextForMatch(String(e.MaNhanVien ?? '')),
        }))
      : [];
    return { depts, emps };
  }, [orgData, isShowEmployee, nonManagerEmployees]);

  useEffect(() => {
    const kw = debouncedSearchTerm.trim();
    const gen = searchGenRef.current;

    if (!kw || kw.length < SEARCH_MIN_CHARS) {
      startTransition(() => {
        if (gen !== searchGenRef.current) return;
        setSearchMatch(null);
      });
      return;
    }

    let cancelled = false;
    const run = async () => {
      const kwNorm = normalizeSearchTextForMatch(kw);
      if (!kwNorm) {
        if (cancelled || gen !== searchGenRef.current) return;
        startTransition(() => {
          if (gen !== searchGenRef.current) return;
          setSearchMatch(null);
        });
        return;
      }

      const deptIds = new Set<string>();
      const deptTitleIds = new Set<string>();
      const empIds = new Set<string>();
      const empDeptIdSet = new Set<string>();
      const currentEmployeeId = profile?.id ? String(profile.id) : null;

      for (let i = 0; i < searchIndex.depts.length; i++) {
        if (i > 0 && i % SEARCH_CHUNK_SIZE === 0) {
          await yieldToMain();
          if (cancelled || gen !== searchGenRef.current) return;
        }
        const d = searchIndex.depts[i];
        if (d.normTitle.includes(kwNorm)) {
          deptIds.add(d.id);
          deptTitleIds.add(d.id);
        }
      }

      if (isShowEmployee) {
        for (let i = 0; i < searchIndex.emps.length; i++) {
          if (i > 0 && i % SEARCH_CHUNK_SIZE === 0) {
            await yieldToMain();
            if (cancelled || gen !== searchGenRef.current) return;
          }
          const e = searchIndex.emps[i];
          if (!e.normTitle.includes(kwNorm) && !e.normMa.includes(kwNorm)) continue;
          if (isNhanVien && !ngangcap && currentEmployeeId && e.id !== currentEmployeeId) continue;
          deptIds.add(e.depId);
          empIds.add(e.id);
          if (e.depId) empDeptIdSet.add(e.depId);
        }
      }

      if (cancelled || gen !== searchGenRef.current) return;
      startTransition(() => {
        if (gen !== searchGenRef.current) return;
        setSearchMatch({
          deptIds,
          deptTitleIds,
          empIds,
          empDeptIds: Array.from(empDeptIdSet),
        });
      });
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearchTerm, searchIndex, isShowEmployee, isNhanVien, ngangcap, profile?.id]);

  const isSearchActive = searchMatch != null && Boolean(debouncedSearchTerm.trim());

  useEffect(() => {
    setSearchExpandedKeys([]);
  }, [debouncedSearchTerm]);

  const displayTreeData = useMemo(() => {
    if (!searchMatch) return treeData;
    const byId = new Map(treeData.map((n) => [n.id, n]));
    const keep = new Set<string>();
    for (const id of searchMatch.deptIds) {
      let cur: string | undefined = id;
      while (cur && cur !== '0' && byId.has(cur) && !keep.has(cur)) {
        keep.add(cur);
        const node = byId.get(cur);
        const nextPid = node ? node.pId : undefined;
        cur = nextPid && nextPid !== '0' ? String(nextPid) : undefined;
      }
    }
    const filtered = treeData.filter((n) => keep.has(n.id));
    const hasChild = new Set<string>();
    for (const n of filtered) {
      if (n.pId && n.pId !== '0') hasChild.add(n.pId);
    }
    const empDeptSet = new Set(searchMatch.empDeptIds);
    return filtered.map((n) => ({
      ...n,
      isLeaf: !hasChild.has(n.id) && !(isShowEmployee && empDeptSet.has(n.id)),
    }));
  }, [treeData, searchMatch, isShowEmployee]);

  const searchAutoExpandKeys = useMemo(() => {
    if (!isSearchActive || !displayTreeData.length) return [] as string[];
    const ids = new Set(displayTreeData.map((n) => n.id));
    const expand = new Set<string>();
    for (const n of displayTreeData) {
      const p = n.pId ?? '';
      if (p && p !== '0' && ids.has(p)) expand.add(p);
    }
    if (isShowEmployee && searchMatch) {
      for (const id of searchMatch.empDeptIds) {
        if (ids.has(id)) expand.add(id);
      }
    }
    return Array.from(expand);
  }, [isSearchActive, displayTreeData, isShowEmployee, searchMatch]);

  const effectiveTreeExpandedKeys = isSearchActive
    ? Array.from(new Set([...searchAutoExpandKeys, ...searchExpandedKeys]))
    : expandedKeys;

  // Chỉ hiển thị node gốc + con của các node đã expand (lazy). Search cũng vậy — không dump hết kết quả.
  const visibleTreeData = useMemo(() => {
    if (!displayTreeData.length) return [];
    const expandedSet = new Set(effectiveTreeExpandedKeys);
    const allIds = new Set(displayTreeData.map((n) => n.id));
    const childrenByPid = new Map<string, SimpleTreeNode[]>();
    const roots: SimpleTreeNode[] = [];
    for (const n of displayTreeData) {
      const pid = n.pId ?? '';
      const isRoot = !pid || pid === '0' || !allIds.has(pid);
      if (isRoot) {
        roots.push(n);
        continue;
      }
      const list = childrenByPid.get(pid) ?? [];
      list.push(n);
      childrenByPid.set(pid, list);
    }
    const visible: SimpleTreeNode[] = [];
    const walk = (nodes: SimpleTreeNode[]) => {
      for (const n of nodes) {
        visible.push(n);
        if (!expandedSet.has(n.id)) continue;
        const kids = childrenByPid.get(n.id);
        if (kids?.length) walk(kids);
      }
    };
    walk(roots);
    return visible;
  }, [displayTreeData, effectiveTreeExpandedKeys]);

  /** Các phòng được mở nhóm NV do search, để trả lại trạng thái cũ khi xoá từ khoá */
  const searchOpenedEmpDeptsRef = useRef<string[]>([]);

  // Khi search: không auto-mở mọi nhóm NV (rất nặng). Chỉ gắn NV khớp trong treeDataWithEmployees.
  // Xoá từ khoá: đóng lại đúng những nhóm NV đã tự mở khi search (nếu còn)
  useEffect(() => {
    if (debouncedSearchTerm.trim()) return;
    const opened = searchOpenedEmpDeptsRef.current;
    if (!opened.length) return;
    searchOpenedEmpDeptsRef.current = [];
    setEmployeeExpandedKeys((prev) => prev.filter((id) => !opened.includes(id)));
  }, [debouncedSearchTerm]);

  //* === EMPLOYEE NODE ===
  useEffect(() => {
    if (vanHoaCompanyOnlyTree) return;
    if (!isNhanVien || !orgData || !bpOriID || !profile?.id) return;
    if (!orgData || !bpOriID || !profile?.id) return;
    if (!isNhanVien && !lockEmployeeInDropdown) return;

    console.log('useEffect 3');
    const currentValue = value?.value;
    const isEmployeeNode = currentValue && typeof currentValue === 'string' && currentValue.startsWith('emp_');
    const bpOriIDStr = String(bpOriID);

    const isSelfEmpNode = isSelfEmployeeSelection(currentValue, profile?.id, bpOriID);

    // Trường hợp 1: employee node trên route không cho chọn NV — giữ label NV hiện tại (buoc4/buoc5)
    if (isEmployeeNode && !isShowEmployee && isSelfEmpNode) {
      return;
    }

    // Trường hợp 1b: employee node khác / không phải self → map về bộ phận của NV đó
    if (isEmployeeNode && !isShowEmployee && treeData.length > 0) {
      const depIdFromEmp =
        typeof currentValue === 'string' && currentValue.includes('_dep_')
          ? currentValue.split('_dep_')[1]
          : '';

      let validDeptNode: SimpleTreeNode | undefined;
      if (depIdFromEmp) {
        validDeptNode = treeData.find((node) => node.id === depIdFromEmp);
      }

      // Fallback: bộ phận profile (vuotcap) hoặc node đầu tiên
      if (!validDeptNode && vuotcap) {
        validDeptNode = treeData.find((node) => node.id === bpOriIDStr);
      }
      if (!validDeptNode && treeData.length > 0) {
        validDeptNode = treeData[0];
      }

      if (validDeptNode) {
        const deptValue = {
          value: validDeptNode.id,
          label: validDeptNode.title,
        };
        setValue(deptValue);
        onChange?.(deptValue);

        // Set lại storage với node phòng ban
        const allDept = orgData.departments || [];
        const department = Array.isArray(allDept)
          ? allDept.find((d: any) => String(d.ID ?? d.id) === validDeptNode?.id)
          : Object.values(allDept).find((d: any) => String(d.ID ?? d.id) === validDeptNode?.id);

        if (department) {
          setTreeSelectDepartment({
            selectedValue: deptValue,
            tenantID: tenantID || '',
            idSelect: validDeptNode.id,
            isEmployee: false,
            parentID: String((department as any).ParentID ?? (department as any).parentID ?? validDeptNode.pId ?? '0'),
            isCompany:
              (department as any).Key === 1 || (department as any).IsCompany || (department as any).isCompany || validDeptNode.isCompany || false,
          });
        }
      }
      return;
    }

    // Trường hợp 2: Chưa có value → auto chọn NV hiện tại
    if (!value) {
      const allEmp = orgData.employees.filter((e: any) => !(e?.IsQuanLy ?? e?.isQuanLy)) || [];
      const currentEmployeeId = String(profile.id);

      const currentEmployee = allEmp.find(
        (e: any) => String(e.ID ?? e.id) === currentEmployeeId && String(e.BPID ?? e.bpid ?? '') === bpOriIDStr
      );

      const empLabel = String(
        currentEmployee?.TenNhanVien ?? profile?.tenNhanVien ?? ''
      );
      if (!empLabel && !lockEmployeeInDropdown && !isShowEmployee) return;

      const empValue = {
        value: `emp_${currentEmployeeId}_dep_${bpOriIDStr}`,
        label: empLabel,
      };
      setValue(empValue);
      onChange?.(empValue);
      const allDept = orgData.departments || [];
      const bpDept = Array.isArray(allDept)
        ? allDept.find((d: any) => String(d.ID ?? d.id) === bpOriIDStr)
        : undefined;
      setTreeSelectDepartment({
        selectedValue: empValue,
        tenantID: tenantID || '',
        idSelect: currentEmployeeId,
        isEmployee: true,
        parentID: String((bpDept as any)?.ParentID ?? (bpDept as any)?.parentID ?? '0'),
        isCompany: false,
      });

      if (vuotcap && isShowEmployee) {
        setExpandedKeys([bpOriIDStr]);
        setEmployeeExpandedKeys([bpOriIDStr]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isNhanVien,
    value?.value,
    treeData,
    isShowEmployee,
    vuotcap,
    orgData,
    bpOriID,
    profile?.id,
    tenantID,
    vanHoaCompanyOnlyTree,
  ]);

  // Auto select first node if needed
  // Chỉ auto-select khi không có externalValue và không có value
  useEffect(() => {
    if (!autoSelectFirst || value || externalValue || !treeData.length) return;
    console.log('useEffect 4');
    const first = treeData[0];
    const newVal = { value: first.id, label: first.title };
    setValue(newVal);
    onChange?.(newVal);
    // Không ép ghi vào global store ở đây, để DropDownDepartment lo phần init
    // chỉ nhằm hiển thị mặc định khi chưa có gì
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectFirst, treeData, externalValue]);

  const handleSelect = useCallback(
    (selected: any, option: any) => {
      const rawVal = selected?.value ?? selected;
      const rawLabel = option?.title ?? option?.label ?? selected?.label ?? rawVal;
      const labelText = extractTextLabel(rawLabel);
      const newValue = { value: rawVal, label: labelText };

      setValue(newValue);
      onChange?.(newValue);

      const stringVal = String(rawVal ?? '');
      const isEmployee = stringVal.startsWith('emp_');
      let idSelect = stringVal;
      let parentID: string = '0';

      if (isEmployee && stringVal.includes('_dep_')) {
        const parts = stringVal.split('_dep_');
        idSelect = parts[0].replace('emp_', '');
        parentID = parts[1] ?? '0';
      } else {
        parentID = option?.pId ?? '0';
      }

      setTreeSelectDepartment({
        selectedValue: newValue,
        tenantID,
        idSelect,
        isEmployee,
        parentID,
        isCompany: option?.isCompany ?? false,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, routeCongTy, setTreeSelectDepartment, tenantID]
  );

  // Khi đã có giá trị hiện tại -> auto expand path từ root đến current (dựa vào ParentID/TreePath)
  useEffect(() => {
    console.log('useEffect 5');
    if (!orgData || !value?.value) return;

    const rawVal = String(value.value);
    const isEmployee = rawVal.startsWith('emp_') && rawVal.includes('_dep_');
    const depId = isEmployee ? rawVal.split('_dep_')[1] : rawVal;
    if (!depId) return;

    const ancestors = getAncestorIds(depId, departments);

    setExpandedKeys((prev) => Array.from(new Set([...prev, ...ancestors, depId])));

    // nếu đang chọn nhân viên thì luôn mở nhóm nhân viên của phòng đó
    if (isEmployee) {
      setEmployeeExpandedKeys((prev) => (prev.includes(depId) ? prev : [...prev, depId]));
    }
  }, [orgData, value?.value, departments]);

  // build treeData với employee nodes (chỉ từ visibleTreeData = lazy load khi expand)
  const treeDataWithEmployees = useMemo(() => {
    if (!orgData || !isShowEmployee) return visibleTreeData;
    const result: SimpleTreeNode[] = [];
    const currentEmployeeId = profile?.id ? String(profile.id) : null;
    const bpOriIDStr = bpOriID ? String(bpOriID) : null;
    const searchEmpDeptSet =
      isSearchActive && searchMatch ? new Set(searchMatch.empDeptIds) : null;
    const expandedSet = new Set(effectiveTreeExpandedKeys);
    const shouldShowEmployeesUnder = (depId: string) => {
      if (employeeExpandedKeys.includes(depId)) return true;
      if (isSearchActive && searchEmpDeptSet?.has(depId) && expandedSet.has(depId)) return true;
      return false;
    };

    const empsOfDept = (depId: string) => empsByDeptId.get(depId) ?? [];

    const phanHe = localStorage.getItem('phanHe') ?? 'bsc';
    
    const filterEmpBySearchIfNeeded = (list: any[]) => {
      const listByPhanHe = list.filter((e: any) => String(e.PhanHe ?? '').split(',').includes(phanHe));
      console.log('list', list);
      console.log('phanHe', phanHe);
      console.log('listByPhanHe', listByPhanHe);
      if (!isSearchActive || !searchMatch) return listByPhanHe;
      return listByPhanHe.filter((e: any) => searchMatch.empIds.has(String(e.ID ?? e.id)));
    };

    // isNhanVien không có vuotcap: không có node phòng ban, chỉ có node nhân viên ở root
    if (isNhanVien && !vuotcap && currentEmployeeId && bpOriIDStr) {
      const empInDept = empsOfDept(bpOriIDStr);
      // ngangcap: hiển thị tất cả nhân viên cùng phòng (root level). Không ngangcap: chỉ bản thân
      let toShow = ngangcap ? empInDept : empInDept.filter((e: any) => String(e.ID ?? e.id) === currentEmployeeId);
      toShow = filterEmpBySearchIfNeeded(toShow);
      toShow.forEach((e: any) => {
        const eid = String(e.ID ?? e.id);
        const empNodeId = `emp_${eid}_dep_${bpOriIDStr}`;
        result.push({
          id: empNodeId,
          pId: bpOriIDStr,
          title: `${String(e.TenNhanVien ?? '')}${(String(e.MaNhanVien ?? '') != '' ? ` (${String(e.MaNhanVien ?? '') })` : '') }`,
          isLeaf: true,
          key: empNodeId,
        });
      });
      return result;
    }

    // Logic bình thường: employee nodes nằm trong department (chỉ từ visible nodes)
    const buildTreeRecursive = (parentId: string | undefined): void => {
      const childDepts = visibleTreeData.filter((d) => d.pId === parentId);

      childDepts.forEach((dep) => {
        result.push(dep);

        if (shouldShowEmployeesUnder(dep.id)) {
          let empOfDept = empsOfDept(dep.id);
          if (isNhanVien && currentEmployeeId && !ngangcap) {
            // Nhân viên không có quyền ngang cấp: chỉ thấy chính mình
            empOfDept = empOfDept.filter((e: any) => String(e.ID ?? e.id) === currentEmployeeId);
          }
          empOfDept = filterEmpBySearchIfNeeded(empOfDept);
          empOfDept.forEach((e: any) => {
            const empNodeId = `emp_${e.ID ?? e.id}_dep_${dep.id}`;
            result.push({
              id: empNodeId,
              pId: dep.id,
              title: `${String(e.TenNhanVien ?? '')}${(String(e.MaNhanVien ?? '') != '' ? ` (${String(e.MaNhanVien ?? '')})` : '')}`,
              isLeaf: true,
              key: empNodeId,
            });
          });
        }

        buildTreeRecursive(dep.id);
      });
    };

    const allNodeIds = new Set(visibleTreeData.map((d) => d.id));
    const rootNodes = visibleTreeData.filter((d) => !d.pId || !allNodeIds.has(d.pId));

    rootNodes.forEach((root) => {
      result.push(root);

      if (shouldShowEmployeesUnder(root.id)) {
        let empOfDept = empsOfDept(root.id);
        if (isNhanVien && currentEmployeeId && !ngangcap) {
          // Nhân viên không có quyền ngang cấp: chỉ thấy chính mình
          empOfDept = empOfDept.filter((e: any) => String(e.ID ?? e.id) === currentEmployeeId);
        }
        empOfDept = filterEmpBySearchIfNeeded(empOfDept);
        empOfDept.forEach((e: any) => {
          const empNodeId = `emp_${e.ID ?? e.id}_dep_${root.id}`;
          result.push({
            id: empNodeId,
            pId: root.id,
            title: `${String(e.TenNhanVien ?? '')}${(String(e.MaNhanVien ?? '') != '' ? ` (${String(e.MaNhanVien ?? '')})` : '')}`,
            isLeaf: true,
            key: empNodeId,
          });
        });
      }

      buildTreeRecursive(root.id);
    });

    return result;
  }, [
    orgData,
    visibleTreeData,
    employeeExpandedKeys,
    isShowEmployee,
    isNhanVien,
    profile?.id,
    profile?.tenNhanVien,
    bpOriID,
    viewModeParsed.vuotcap,
    ngangcap,
    searchMatch,
    isSearchActive,
    empsByDeptId,
    effectiveTreeExpandedKeys,
  ]);

  const toggleTreeExpandKey = useCallback(
    (id: string) => {
      const setter = isSearchActive ? setSearchExpandedKeys : setExpandedKeys;
      setter((prev) => (prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]));
    },
    [isSearchActive],
  );

  const ensureTreeExpandKey = useCallback(
    (id: string) => {
      const setter = isSearchActive ? setSearchExpandedKeys : setExpandedKeys;
      setter((prev) => (prev.includes(id) ? prev : [...prev, id]));
    },
    [isSearchActive],
  );

  const toggleEmployeeGroup = useCallback(
    (depId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setEmployeeExpandedKeys((prev) => (prev.includes(depId) ? prev.filter((k) => k !== depId) : [...prev, depId]));
      ensureTreeExpandKey(depId);
    },
    [ensureTreeExpandKey],
  );

  const renderTitle = useCallback(
    (node: any) => {
      const id: string = node.id;
      const title: string = node.title;
      const isLeaf = !!node.isLeaf;

      // employee node: đen khi chưa mở dropdown, #0C68E9 khi mở
      if (typeof id === 'string' && id.startsWith('emp_')) {
        return (
          <span
            className="employee-node"
            style={{
              color: dropdownOpen ? '#0C68E9' : 'black',
              fontWeight: 500,
            }}
          >
            {title}
          </span>
        );
      }

      const hasEmployees =
        isShowEmployee &&
        (isSearchActive
          ? Boolean(searchMatch && searchMatch.empDeptIds.includes(id))
          : deptIdsWithEmployees.has(id));
      const shouldHideEmployeeIcon = node.hideEmployeeIcon === true;
      const isExpanded = effectiveTreeExpandedKeys.includes(id);
      const employeeGroupOpen = employeeExpandedKeys.includes(id);

      const handleToggleExpand = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isLeaf) return;
        toggleTreeExpandKey(id);
      };

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <span style={{ flex: 1 }}>{title}</span>
          {hasEmployees && !shouldHideEmployeeIcon && (
            <Button
              type="text"
              size="small"
              onClick={(e) => toggleEmployeeGroup(id, e)}
              className="!ml-1 !min-w-auto !h-5 !flex-shrink-0"
              icon={
                <TeamOutlined
                  style={{ fontSize: 12, color: employeeGroupOpen ? '#0C68E9' : '#919eab' }}
                />
              }
            />
          )}

          {!isLeaf && (
            <span
              onClick={handleToggleExpand}
              className="ml-1 flex items-center w-10 h-10 rounded-lg justify-center cursor-pointer hover:bg-[rgba(0,0,0,0.08)] custom-icon-expand"
            >
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </span>
          )}
        </div>
      );
    },
    [
      effectiveTreeExpandedKeys,
      isShowEmployee,
      isSearchActive,
      searchMatch,
      deptIdsWithEmployees,
      toggleEmployeeGroup,
      toggleTreeExpandKey,
      dropdownOpen,
      employeeExpandedKeys,
    ]
  );

  // Kiểm tra xem có root node (pId = '0' hoặc null/undefined) trong treeDataWithEmployees không
  const hasRootNode = useMemo(() => {
    return treeDataWithEmployees.some(
      (node) => !node.pId || node.pId === '0' || node.pId === null || node.pId === undefined
    );
  }, [treeDataWithEmployees]);

  const treeSelectValue = useMemo(() => {
    const emptyPlaceholder = { value: '', label: placeholder || 'Chọn bộ phận' };

    if (loading || !orgData || !value?.value) {
      return emptyPlaceholder;
    }

    if (lockEmployeeInDropdown && value.label?.trim()) {
      return value;
    }

    if (value.label?.trim() && value.label !== value.value) {
      return value;
    }

    if (treeDataWithEmployees.length > 0) {
      return treeDataWithEmployees.some((n) => n.id === value.value) ? value : emptyPlaceholder;
    }

    if (value.label?.trim()) {
      return value;
    }

    return emptyPlaceholder;
  }, [loading, orgData, value, lockEmployeeInDropdown, treeDataWithEmployees, placeholder]);

  if (!visible) return null;
  return (
    <Spin size="small" spinning={loading} className="w-fit">
      <div className="flex items-center text-[#919EAB]">
        <TreeSelect
          open={dropdownOpen}
          onDropdownVisibleChange={setDropdownOpen}
          className="custom-tree-select"
          classNames={{ popup: { root: 'custom-tree-select-dropdown' } }}
          popupMatchSelectWidth={false}
          variant="borderless"
          value={treeSelectValue}
          labelInValue
          onChange={(v) => {
            setValue(v as any);
            if (onChange) onChange(v as any);
          }}
          onSelect={handleSelect}
          treeLine={{ showLeafIcon: false }}
          switcherIcon={() => null}
          suffixIcon={<SelectIco />}
          placeholder={placeholder}
          popupRender={(menu) => (
            <div className="custom-tree-select-dropdown-wrapper">
              {!(vanHoaCompanyOnlyTree && treeData.length <= 1) && (
                <SearchField
                  draftRef={searchDraftRef}
                  onCancelInFlight={cancelInFlightSearch}
                  onDebouncedChange={handleDebouncedSearch}
                />
              )}
              {menu}
            </div>
          )}
          styles={{
            popup: { root: { maxHeight: 620, overflow: 'auto' } } as any,
          }}
          treeDataSimpleMode
          fieldNames={{ value: 'id', label: 'title' }}
          treeData={treeDataWithEmployees as any}
          treeExpandedKeys={effectiveTreeExpandedKeys}
          onTreeExpand={(keys) => {
            const k = keys as string[];
            if (isSearchActive) {
              setSearchExpandedKeys(Array.from(new Set([...k, ...searchAutoExpandKeys])));
            } else {
              setExpandedKeys(k);
            }
          }}
          treeTitleRender={renderTitle}
          filterTreeNode={false}
        />
      </div>

      {/* ======= Custom styles Search Input ======= */}
      <style>{`
        .custom-tree-select {
          height: 22px!important;
        }
        .custom-tree-select .ant-select-selector {
          background-color: #919EAB29;
          font-size: 12px;
          font-weight: 700;
          margin-right: 5px;
          padding-right: 25px !important;
        }
        .custom-tree-select .ant-select-selection-item {
          padding-inline-end: 0!important;
          font-size: 12px;
          font-weight: 700;
        }
        .custom-tree-select .ant-select-selector .custom-icon-expand {
          display: none!important;
        }
        .custom-tree-select-dropdown .ant-select-tree-node-content-wrapper .ant-select-tree-node-content-wrapper-normal {
          border-top-left-radius: 0!important;
          border-bottom-left-radius: 0!important;
          width: 100%;
        }
        .custom-tree-select .ant-select-tree-node-selected {
          background-color: #0C68E9!important;
          color: #ffffff!important;
        }
        /* node nhân viên được chọn: bg xanh, chữ trắng */
        .custom-tree-select-dropdown .ant-select-tree-node-selected:has(.employee-node) {
          background-color: #0C68E9!important;
          color: #ffffff!important;
        }
        .custom-tree-select-dropdown .ant-select-tree-node-selected:has(.employee-node) .employee-node {
          color: #ffffff!important;
        }
        .custom-tree-select .ant-select-tree-node-selected:hover,
        .custom-tree-select-dropdown .ant-select-tree-node-selected:hover {
          color: #ffffff!important;
        }
        .custom-tree-select-dropdown .ant-select-tree-node-selected:has(.employee-node):hover .employee-node {
          color: #ffffff!important;
        }
        /* icon nhân viên màu trắng khi node được chọn */
        .custom-tree-select-dropdown .ant-select-tree-node-selected .anticon-team {
          color: #ffffff!important;
        }
        .custom-tree-select-dropdown {
          min-width: 300px!important;
          padding: 15px!important;
        }
        .custom-tree-select-dropdown .custom-tree-select-dropdown-wrapper {
          display: flex;
          flex-direction: column;
        }
        .custom-tree-select-dropdown .ant-select-tree-treenode {
          display: flex;
          align-items: flex-start;
          margin-bottom: -9px;
          line-height: 23px;
          position: relative;
          border-radius: 8px!important;
          overflow: visible!important;
        }
        .ant-tree-select-dropdown .ant-select-tree .ant-select-tree-treenode {
          background-color: transparent!important;
          margin-bottom: 0px!important;
        } 
        /* Ẩn icon mũi tên mặc định, nhưng giữ switcher & pseudo ::before để vẽ line */
        .custom-tree-select-dropdown .ant-select-tree-switcher:not(.ant-select-tree-switcher-noop) svg {
          width: 0!important;
          height: 0!important;
        }
        .custom-tree-select-dropdown .ant-select-tree-switcher:before {
          height: 100%!important;
          width: 100%!important;
        }
        .custom-tree-select-dropdown .ant-select-tree-title {
          line-height: 42px !important;
          height: 42px !important;
          font-weight: 600 !important;
        }
        .custom-tree-select-dropdown.ant-tree-select-dropdown .ant-select-tree .ant-select-tree-switcher-leaf-line:after {
          background-image: url('/images/vector-line.png');
          background-repeat: no-repeat;
          background-size: contain;
          border-bottom: none!important;
          width: 12px!important;
          height: 12px!important;
          top: 12px!important;
          left: 11px!important;
        }
        .custom-tree-select-dropdown  .ant-select-tree-switcher:before {
          content: '';
          position: absolute;
          left: 11px !important;
          top: 0px !important;
          width: 1px!important;
          background-color: #e4e5e9;
        }
        .custom-tree-select-dropdown  .ant-select-tree-switcher:after {
          content: '';
          position: absolute;
          left: 11px;
          top: 12px;
          width: 12px;
          height: 12px;
          background-image: url('/images/vector-line.png');
          background-repeat: no-repeat;
          background-size: contain;
          z-index: 1;
        }
        .custom-tree-select-dropdown .ant-select-tree-treenode-leaf-last .ant-select-tree-switcher:before {
          height: 20px !important;
        }
        ${
          hasRootNode
            ? `
        .custom-tree-select-dropdown .ant-select-tree-list-holder-inner > div:first-of-type .ant-select-tree-switcher {
          display: none !important;
        }
        .custom-tree-select-dropdown .ant-select-tree-list-holder-inner > div:first-of-type .ant-select-tree-node-content-wrapper {
          padding-left: 22px !important;        
        }
        `
            : ''
        }
      `}</style>
    </Spin>
  );
};

export default DepEmpTreeSelect;